import React, { useCallback, useEffect, useState } from 'react';
import GoodsCreateView from '../Views/GoodsCreateView';
import { CustomModal, useCustomToast } from '../../../../components';
import { useCreateGoods } from '../../../../hooks';
import { UploadedFile, useGoodsRequestFormik } from '../../../../utils';
import { GoodsRequest, GoodTypes } from '../../types';
import { useAtom } from 'jotai';
import { authAtom } from '../../../../state';
import {
  useGetDependeciesByUserLogged,
  useGetUtilsKeys,
  useGoodsConsumerGoods,
} from '../../../../share';
import ModalSaveChanges from '../../../../components/ModalSaveChanges/ModalSaveChanges';

interface GoodsCreateProps {
  isOpen: boolean;
  OnClose: () => void;
}

const GoodsCreateContainer: React.FC<GoodsCreateProps> = ({
  isOpen,
  OnClose,
}) => {
  const { showToast } = useCustomToast();
  const [files, setFiles] = React.useState<UploadedFile[]>();
  const [items, setItems] = React.useState<any[]>();
  const [isOpenSaveChanges, setiIsOpenSaveChanges] = useState(false)

  const [typeRequest, setTypeRequest] = useState(0);

  const [authData] = useAtom(authAtom);
  const { mutateAsync, isPending: loadCreate } = useCreateGoods();
  const { data: dependenciesUser } = useGetDependeciesByUserLogged();
  const { data: consumerGoods } = useGoodsConsumerGoods();
  const { data: locations = [] } = useGetUtilsKeys(
    'keys[]=locations&filter[branch_id]='+authData?.user?.branch_id,
  );
  const { data: utilKeys = [] } = useGetUtilsKeys(
  'keys[]=asset_type&keys[]=items',
  );

  const closeModalSaveChanges = () => {
    setiIsOpenSaveChanges(false);
  }
  const onSave = async (values: GoodsRequest) => {
    if (typeRequest == GoodTypes.ACTIVO_FIJO_ID) {
      await mutateAsync({
        ...values,
        goods_location_id: values.location,
        documents: files,
        request_type: values.request_type,
        items: items?.map((x) => ({
          itemable_id: x.id.toString(),
          quantity_requested: 1,
          status_id: x.status_id,
          observations: x.observation,
        })),
      });
    } else if (typeRequest == GoodTypes.BIEN_CONSUMO_ID) {
      await mutateAsync({
        ...values,
        goods_location_id: values.location,
        documents: files,
        request_type: values.request_type,
        items: items?.map((x: any) => ({
          itemable_id: x.id.toString(),
          quantity_requested: Number(x.quantity || 1),
          status_id: x.status?.id,
          observations: '',
        })),
      });
    }
  };

  const formik = useGoodsRequestFormik({ onSubmit: onSave });

  const hanbdleTypeRequest = (type: number) => {
    setTypeRequest(type)
  }
  const onReset = useCallback(() => {
    closeModalSaveChanges();
    formik.resetForm();
    OnClose();
    setFiles([]);
    setItems([]);
  }, [formik, OnClose]);

  const handleAddItem = (item: any) => {
    const { item_quantity, goods_type } = formik.values;
  
    if (item_quantity && item_quantity >= 1) {
      const isActivoFijo = Number(goods_type) === GoodTypes.ACTIVO_FIJO_ID;
      const itemsToAdd = isActivoFijo
        ? Array.from({ length: item_quantity }, (_, i) => ({ ...item, uniqueKey: `${item.id}-${i}`, }))
        : [{...item}];

      onResetItem();
      setItems((prev) => [...(prev || []), ...itemsToAdd]);
    }
  };

  const onResetItem = () => {
    formik.setFieldValue('item_id', '');
    formik.setFieldValue('item_quantity', '');
  };

  const handleDeleteItem = (req: number) => {
    const updatedItems = items?.filter((_, index) => index !== req);
    setItems(updatedItems);
    onResetItem();
  };

  //Levantar modal de confirmacion
  const validateAndOpenModal = useCallback(async () => {
    const errors = await formik.validateForm();
    formik.setTouched(
      Object.keys(formik.initialValues).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      )
    );
    if (Object.keys(errors).length === 0) {
      setiIsOpenSaveChanges(true);
    }
  }, [formik]);

  const confirmSaveChanges = useCallback(async () => {
    try {
      closeModalSaveChanges();
      await onSave(formik.values);
      onReset();
    } catch (error: any) {
      showToast('error', String(error?.message) || 'Error al crear solicitud');
    }
  }, [onSave, formik, onReset]);

  useEffect(() => {
    if (dependenciesUser?.[0]?.id) {
      formik.setFieldValue('dependency_id', dependenciesUser[0].id);
    }
  }, [dependenciesUser, isOpen]);

  return (
    <CustomModal
      title={`Creación de solicitud ${typeRequest == GoodTypes.ACTIVO_FIJO_ID ? "de activos" : typeRequest == GoodTypes.BIEN_CONSUMO_ID ? "de bienes" : ""}`}
      labelSubmit="Crear solicitud"
      open={isOpen}
      onCancel={() => {
        onReset();
      }}
      width="95vw"
      isLoadingSave={loadCreate}
      onSave={validateAndOpenModal}
    >
      <GoodsCreateView
        location={locations?.locations || []}
        itemsAdded={items}
        items={utilKeys?.items || []}
        setItems={setItems}
        itemTypes={utilKeys?.asset_type || []}
        authData={authData}
        dependencies={dependenciesUser || []}
        formik={formik}
        onFiles={setFiles}
        files={files}
        onAdd={handleAddItem}
        onDeleteItem={handleDeleteItem}
        hanbdleTypeRequest={hanbdleTypeRequest}
        goodConsumos={consumerGoods || []}
      />
      <ModalSaveChanges
        title="Guardar cambios"
        textOne={`¿Está seguro de crear esta solicitud de ${typeRequest == GoodTypes.ACTIVO_FIJO_ID ? GoodTypes.ACTIVO_FIJO : GoodTypes.BIEN_CONSUMO}?`}
        open={isOpenSaveChanges}
        width={900}
        isLoadingSave={false}
        onClose={closeModalSaveChanges}
        onCancel={closeModalSaveChanges}
        labelSubmit="Aceptar"
        onSave={confirmSaveChanges}
        />
    </CustomModal>
  );
};

export default GoodsCreateContainer;
